import React, { useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { LoadingButton } from "@mui/lab";
import skuServices from "app/services/sku-services";
import SkuBinStockPanel from "./SkuBinStockPanel";
import { useSnackbar } from "notistack";
import { HOST } from "app/utils/constants/paths";
import moment from "moment";

const defaultRetailCodeRow = () => ({ retail_name: "", barcode: "" });

const formatForInput = (value) => {
    if (!value) return "";
    return moment(value).format("YYYY-MM-DDTHH:mm");
};

const buildImageSrc = (imageUrl) => {
    if (!imageUrl) return "";
    return imageUrl.startsWith("http") ? imageUrl : `${HOST}${imageUrl}`;
};

const SKUEditDrawer = ({
    record = null,
    onClose = () => {},
    onUpdated = () => {},
}) => {
    const open = Boolean(record);
    const { enqueueSnackbar } = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [formState, setFormState] = useState({
        sku: "",
        barcode: "",
        last_scanned_at: "",
        first_manual_pick_at: "",
        image: null,
    });
    const [retailCodes, setRetailCodes] = useState([defaultRetailCodeRow()]);

    useEffect(() => {
        if (record) {
            setFormState({
                sku: record.sku || "",
                barcode: record.barcode || "",
                last_scanned_at: formatForInput(record.last_scanned_at),
                first_manual_pick_at: formatForInput(record.first_manual_pick_at),
                image: null,
            });
            setRetailCodes(
                record.retail_codes?.length
                    ? record.retail_codes.map((rc) => ({ id: rc.id, retail_name: rc.retail_name || "", barcode: rc.barcode || "" }))
                    : [defaultRetailCodeRow()]
            );
            setSaving(false);
        }
    }, [record]);

    const visibleRetailCodes = useMemo(
        () => retailCodes.map((rc, index) => ({ ...rc, _index: index })).filter((rc) => !rc._destroy),
        [retailCodes]
    );

    const canSave = useMemo(() => {
        if (saving) return false;
        const hasMainInfo = Boolean(formState.sku?.trim() || formState.barcode?.trim());
        const hasAnyRetailCode = visibleRetailCodes.some((rc) => rc.retail_name?.trim() || rc.barcode?.trim());
        return hasMainInfo || hasAnyRetailCode;
    }, [formState, visibleRetailCodes, saving]);

    const handleFormFieldChange = (fieldName, fieldValue) => {
        let normalized = fieldValue;
        if (fieldName === "sku" || fieldName === "barcode") normalized = fieldValue.toUpperCase();
        setFormState((prev) => ({ ...prev, [fieldName]: normalized }));
    };

    const handleRetailCodeChange = (index, fieldName, fieldValue) => {
        let normalized = fieldValue;
        if (fieldName === "retail_name" && fieldValue.length > 0) {
            normalized = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
        }
        setRetailCodes((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [fieldName]: normalized };
            return next;
        });
    };

    const addRetailCodeRow = () => {
        setRetailCodes((prev) => [...prev, defaultRetailCodeRow()]);
    };

    const removeRetailCodeRow = (index) => {
        setRetailCodes((prev) => {
            const item = prev[index];
            if (item.id) {
                const next = [...prev];
                next[index] = { ...item, _destroy: true };
                return next;
            }
            const next = [...prev];
            next.splice(index, 1);
            return next.length ? next : [defaultRetailCodeRow()];
        });
    };

    const handleSave = async () => {
        if (!canSave) return;

        try {
            setSaving(true);
            await skuServices.updateSku(record.id, {
                sku: formState.sku,
                barcode: formState.barcode,
                last_scanned_at: formState.last_scanned_at || null,
                first_manual_pick_at: formState.first_manual_pick_at || null,
                image: formState.image,
                retail_codes: retailCodes,
            });
            enqueueSnackbar("SKU actualizado correctamente", { variant: "success" });
            onUpdated();
            onClose();
        } catch (_error) {
            // Interceptor handles generic message.
        } finally {
            setSaving(false);
        }
    };

    const currentImageSrc = buildImageSrc(record?.image_url);

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 500 } } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                <Typography variant="h3" color="primary">Editar SKU</Typography>
                <Tooltip title="Cerrar">
                    <IconButton size="small" onClick={onClose}>
                        <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Divider />

            <Box sx={{ px: 3, py: 2, overflowY: "auto", flex: 1 }}>
                <Stack spacing={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="SKU"
                                value={formState.sku}
                                onChange={(e) => handleFormFieldChange("sku", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Código de barra principal"
                                value={formState.barcode}
                                onChange={(e) => handleFormFieldChange("barcode", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Último escaneo"
                                InputLabelProps={{ shrink: true }}
                                value={formState.last_scanned_at}
                                onChange={(e) => handleFormFieldChange("last_scanned_at", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Primer pickeo manual"
                                InputLabelProps={{ shrink: true }}
                                value={formState.first_manual_pick_at}
                                onChange={(e) => handleFormFieldChange("first_manual_pick_at", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                {currentImageSrc && !formState.image && (
                                    <Avatar
                                        variant="rounded"
                                        src={currentImageSrc}
                                        sx={{ width: 48, height: 48 }}
                                    />
                                )}
                                {formState.image && (
                                    <Avatar
                                        variant="rounded"
                                        src={URL.createObjectURL(formState.image)}
                                        sx={{ width: 48, height: 48 }}
                                    />
                                )}
                                <Button variant="outlined" component="label" size="small">
                                    {currentImageSrc ? "Cambiar foto" : "Subir foto"}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => handleFormFieldChange("image", e.target.files?.[0] || null)}
                                    />
                                </Button>
                                {formState.image && (
                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                                        {formState.image.name}
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>

                    {/* La posición se guarda con su propio endpoint (set_bin) y no
                        con el submit del SKU: son cosas distintas y entrelazarlas
                        obligaría a guardar el producto entero para mover un rack. */}
                    <Divider sx={{ my: 1 }} />
                    <SkuBinStockPanel record={record} onUpdated={onUpdated} />

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Typography variant="h4">Códigos retail</Typography>
                        <Button size="small" startIcon={<AddCircleOutlineOutlinedIcon />} onClick={addRetailCodeRow}>
                            Agregar retail
                        </Button>
                    </Stack>

                    {visibleRetailCodes.map((retailCode) => (
                        <Grid container spacing={2} key={`retail-${retailCode._index}`}>
                            <Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Retail"
                                    value={retailCode.retail_name}
                                    onChange={(e) => handleRetailCodeChange(retailCode._index, "retail_name", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Código de barra retail"
                                    value={retailCode.barcode}
                                    onChange={(e) => handleRetailCodeChange(retailCode._index, "barcode", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <TrashIconButton
                                    onClick={() => removeRetailCodeRow(retailCode._index)}
                                    disabled={visibleRetailCodes.length === 1 && !retailCode.id}
                                />
                            </Grid>
                        </Grid>
                    ))}
                </Stack>
            </Box>

            <Divider />

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Cerrar</Button>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    disabled={!canSave}
                    loadingPosition="end"
                    onClick={handleSave}
                >
                    {saving ? "Guardando" : "Guardar"}
                </LoadingButton>
            </Stack>
        </Drawer>
    );
};

export default SKUEditDrawer;
